import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, OnDestroy, ViewChild, computed, effect, inject, signal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { DropdownComponent } from '@shared/components/ui/dropdown/dropdown.component';
import { DropdownItemComponent } from '@shared/components/ui/dropdown/dropdown-item/dropdown-item.component';
import { NotificationService as NotificationHttpService } from '@shared/services/notification/notification.service';
import { NotificationDTO } from '@models/notification.model';
import { formatRelative } from '@shared/utils/i18n';

@Component({
  selector: 'app-notification-icon',
  imports: [
    CommonModule,
    RouterModule,
    TranslocoModule,
    DropdownComponent,
    DropdownItemComponent
  ],
  templateUrl: './notification-icon.component.html',
})
export class NotificationIconComponent implements OnInit, OnDestroy {
  isOpen = false;

  /** Contenedor scrolleable del listado; se usa para detectar si hace falta rellenar más páginas. */
  @ViewChild('scrollContainer') private scrollContainer?: ElementRef<HTMLUListElement>;

  /** Tamaño de página del scroll infinito del panel. */
  private readonly pageSize = 10;
  /** Cuántas notificaciones se muestran en modo compacto (antes de pulsar "Ver todas"). */
  private readonly compactCount = 4;
  /** Última página ya cargada (-1 = aún no se cargó nada). */
  private loadedPage = -1;

  // Estado paginado local del panel (scroll infinito estilo Facebook). A diferencia de
  // `recentNotifications` del service (topado a 5 para el SSE/badge), aquí acumulamos páginas.
  notifications = signal<NotificationDTO[]>([]);
  loading = signal<boolean>(false);
  hasMore = signal<boolean>(true);
  /** false = cajita compacta con botón "Ver todas"; true = expandida (crece + scroll infinito). */
  expanded = signal<boolean>(false);

  // Computed properties
  notifying = computed(() => this.notificationService.unreadCount() > 0);
  unreadCount = computed(() => this.notificationService.unreadCount());
  /** Notificaciones visibles: pocas en compacto, todas las cargadas al expandir. */
  visibleNotifications = computed(() =>
    this.expanded() ? this.notifications() : this.notifications().slice(0, this.compactCount)
  );
  /** El botón "Ver todas" solo aparece compacto y cuando hay más de lo que se muestra. */
  showSeeAll = computed(
    () => !this.expanded() && (this.notifications().length > this.compactCount || this.hasMore())
  );

  private readonly router = inject(Router);

  constructor(
    private notificationService: NotificationHttpService
  ) {
    // Tiempo real (SSE): cuando llega una notificación nueva, la anteponemos al panel para
    // que aparezca al instante (igual que FB), evitando duplicados por id.
    effect(() => {
      const dto = this.notificationService.lastRealtime();
      if (!dto) {
        return;
      }
      this.notifications.update((list) =>
        list.some((n) => n.id === dto.id) ? list : [dto, ...list]
      );
    });
  }

  ngOnInit(): void {
    // Cargar conteo inicial de notificaciones no leídas
    this.notificationService.refreshUnreadCount();
    // Abrir el stream en tiempo real (SSE): las notificaciones nuevas llegan al instante.
    this.notificationService.connect();
  }

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;

    // Cargar la primera página solo cuando se abre el dropdown.
    if (this.isOpen) {
      this.loadFirstPage();
    }
  }

  /** Reinicia la paginación y carga la primera página al abrir el panel (en modo compacto). */
  private loadFirstPage(): void {
    this.notifications.set([]);
    this.loadedPage = -1;
    this.hasMore.set(true);
    this.expanded.set(false);
    this.fetchPage(0);
  }

  /**
   * "Ver todas": expande la cajita hacia abajo (crece hasta el alto máximo y habilita el
   * scroll infinito) sin salir del panel. No navega; eso ocurre al pulsar una notificación.
   */
  expand(): void {
    this.expanded.set(true);
    // Tras expandir, asegurar que el listado quede scrolleable (cargar más si aún no desborda).
    this.fillUntilScrollable();
  }

  /**
   * Carga la siguiente página al hacer scroll cerca del final del listado (scroll infinito).
   * Solo aplica cuando el panel está expandido y quedan más páginas.
   */
  loadMore(): void {
    if (!this.expanded() || this.loading() || !this.hasMore()) {
      return;
    }
    this.fetchPage(this.loadedPage + 1);
  }

  /** Detecta la proximidad al final del listado para disparar la carga de la siguiente página. */
  onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      this.loadMore();
    }
  }

  /**
   * Rellena el listado hasta que el contenedor desborde (haya scroll real). Sin esto, si el
   * contenido no llena la cajita el evento `scroll` nunca se dispara y las páginas siguientes
   * no se cargarían (bug de scroll infinito en contenedores poco poblados). Patrón estilo FB.
   * Se mide tras el render (setTimeout) y se encadena solo: cada página recién cargada vuelve a
   * comprobar si aún falta llenar.
   */
  private fillUntilScrollable(): void {
    if (!this.expanded() || this.loading() || !this.hasMore()) {
      return;
    }
    setTimeout(() => {
      const el = this.scrollContainer?.nativeElement;
      // Si no hay desbordamiento todavía, cargar otra página (se reencadena al terminar).
      if (el && el.scrollHeight <= el.clientHeight + 1) {
        this.loadMore();
      }
    });
  }

  private fetchPage(page: number): void {
    this.loading.set(true);
    this.notificationService.getAllNotifications(page, this.pageSize, '', 'all').subscribe({
      next: (res) => {
        this.loadedPage = res.page;
        this.hasMore.set(res.page < res.totalPages - 1);
        this.notifications.update((list) => {
          if (page === 0) {
            return res.content;
          }
          // Anteponer/acumular evitando duplicados (p. ej. los que entraron por SSE).
          const known = new Set(list.map((n) => n.id));
          return [...list, ...res.content.filter((n) => !known.has(n.id))];
        });
        this.loading.set(false);
        // Si tras esta página el listado aún no desborda, seguir rellenando (estilo FB).
        this.fillUntilScrollable();
      },
      error: () => {
        // El errorInterceptor ya avisa al usuario; aquí solo se corta el estado de carga.
        this.loading.set(false);
      }
    });
  }

  closeDropdown(): void {
    this.isOpen = false;
  }

  ngOnDestroy(): void {
    // El header se destruye al cerrar sesión → cerramos el stream SSE y sus timers.
    this.notificationService.disconnect();
  }

  markAsRead(notification: NotificationDTO): void {
    if (!notification.isRead) {
      this.notifications.update((list) =>
        list.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n))
      );
      this.notificationService.markAsRead(notification.id).subscribe({
        error: () => this.revertRead(notification),
      });
    }
  }

  /** Deshace la marca optimista de leída si el backend rechazó el cambio. */
  private revertRead(notification: NotificationDTO): void {
    this.notifications.update((list) =>
      list.map((n) => (n.id === notification.id ? { ...n, isRead: false } : n))
    );
  }

  /**
   * Abre una notificación desde el dropdown: la marca como leída (no la elimina; queda
   * visible en estado "leída") y navega al módulo de Notificaciones enfocando la fila exacta
   * (`/notifications?focus=<id>`).
   */
  openNotification(notification: NotificationDTO): void {
    // Optimista: marcar leída en memoria para que el badge baje al instante.
    if (!notification.isRead) {
      this.notifications.update((list) =>
        list.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n))
      );
      this.notificationService.markAsRead(notification.id).subscribe({
        error: () => this.revertRead(notification),
      });
    }
    this.closeDropdown();
    this.router.navigate(['/notifications'], { queryParams: { focus: notification.id } });
  }

  markAllAsRead(): void {
    this.notificationService.markAllAsRead().subscribe({
      next: () => {
        this.notifications.update((list) => list.map((n) => ({ ...n, isRead: true })));
      },
      // El errorInterceptor ya avisa; la lista no cambió (solo se actualiza en `next`).
      error: () => undefined,
    });
  }

  /** Tiempo relativo localizado; delega en el formateador compartido (ver `@shared/utils/i18n`). */
  getTimeAgo(dateString: string): string {
    return formatRelative(dateString, '');
  }

  /**
   * Extrae las iniciales de las primeras 2 palabras del mensaje
   * Ejemplo: "Partner Tech ha solicitado cambios" -> "PT"
   */
  getInitialsFromMessage(message: string): string {
    if (!message) return '??';
    
    const words = message.trim().split(' ');
    if (words.length === 0) return '??';
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    
    // Tomar las primeras 2 palabras
    const firstWord = words[0];
    const secondWord = words[1];
    
    const initial1 = firstWord.charAt(0).toUpperCase();
    const initial2 = secondWord.charAt(0).toUpperCase();
    
    return initial1 + initial2;
  }

}